import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { RegenLogo } from '@/components/RegenLogo';
import { COUNTRIES } from '@/lib/countries';
import { ArrowRight, ArrowLeft, Check, X, Ticket, UserPlus } from 'lucide-react';
import { STEPS, PhotoStep, StepFields, applyOnboardingAnswer, validateOnboardingStep, firstIncompleteStep, onboardingPayload } from './Questionnaire';

const PW_RE = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

const PLAN_LABELS = {
  tracker: 'YourReGen Tracker — Free',
  plus: 'YourReGen+',
  protocol: 'YourReGen Protocol',
};
function billingLabel(plan, billing) {
  if (plan === 'plus') return billing === 'monthly' ? 'Monthly' : 'Annual · 12-month prepaid';
  if (plan === 'protocol') {
    if (billing === '6') return '6 months · paid upfront';
    if (billing === 'monthly' || billing === '1') return '1 month';
    return '12 months · paid upfront';
  }
  return '';
}

// Where a client goes once onboarding is saved: pending paid plan → checkout, otherwise the dashboard.
export function postOnboardingPath(u) {
  if (u?.pending_plan && !u?.subscription_tier) {
    return u.pending_plan === 'regen_plus'
      ? `/checkout?plan=plus&billing=${u.pending_billing || 'annual'}`
      : `/checkout?plan=protocol&billing=${u.pending_billing || '12'}`;
  }
  return '/app/home';
}

export default function OnboardFlow() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, loading, register, refresh } = useAuth();
  const resumed = useRef(false);

  const draft = user?.onboarding_draft || {};
  const plan = (params.get('plan') || draft.plan || '').toLowerCase();
  const billing = (params.get('billing') || draft.billing || '').toLowerCase();
  const planLabel = PLAN_LABELS[plan] || (user?.plan_type === 'access_code' ? 'Access code membership' : '');

  const flowSteps = ['account', ...STEPS];
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [acctError, setAcctError] = useState('');

  // Account step state
  const [acct, setAcct] = useState({ name: '', email: '', phone: '', country: '', password: '' });
  const [confirm, setConfirm] = useState('');
  const [consent, setConsent] = useState({ legal: false, health: false, marketing: false });
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState('');

  // Questionnaire state
  const [data, setData] = useState({});
  const [photos, setPhotos] = useState({});
  const set = (k, v) => setData(d => applyOnboardingAnswer(d, k, v));

  // Entry rules: no product → product selection; finished → next page; signed-in → resume saved answers.
  useEffect(() => {
    if (loading || resumed.current) return;
    resumed.current = true;
    if (user && user.role !== 'client') { nav('/admin/dashboard', { replace: true }); return; }
    if (user?.onboarding_completed) { nav(postOnboardingPath(user), { replace: true }); return; }
    if (!user && !PLAN_LABELS[plan]) { window.location.replace('/#plans'); return; }
    if (user) {
      const saved = { full_name: user.name, country: user.country, ...(user.onboarding_draft?.data || {}) };
      setData(saved);
      setStep(1 + firstIncompleteStep(saved));
    }
  }, [loading, user, plan, nav]);

  const pwValid = PW_RE.test(acct.password);
  const pwMatch = acct.password.length > 0 && acct.password === confirm;
  const allConsent = consent.legal && consent.health;
  const pct = Math.round(((step + 1) / flowSteps.length) * 100);

  const validateAccount = () => {
    if (!acct.name.trim()) { toast.error('Please enter your full name.'); return false; }
    if (!/^\S+@\S+\.\S+$/.test(acct.email)) { toast.error('Please enter a valid email.'); return false; }
    if (!pwValid) { toast.error('Password must be at least 8 characters with one uppercase letter and one number.'); return false; }
    if (!pwMatch) { toast.error('Passwords do not match.'); return false; }
    if (!allConsent) { toast.error('Please accept the required consents to continue.'); return false; }
    return true;
  };

  const saveDraft = (d, nextStep) => api.put('/onboarding/draft', { data: onboardingPayload(d), step: nextStep, plan, billing })
    .catch(() => toast.error('Your answers could not be saved yet — please check your connection.'));

  // The account is created on the first step, so an existing email is caught here — not after the last step.
  const createAccount = async () => {
    setAcctError('');
    setSubmitting(true);
    try {
      await register({
        name: acct.name, email: acct.email.trim().toLowerCase(), phone: acct.phone,
        country: acct.country, password: acct.password,
        access_code: (showCode && code.trim()) ? code.trim().toUpperCase() : undefined,
        consent_terms: true, consent_privacy: true,
        consent_health_data: true, consent_marketing: consent.marketing,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed. Please try again.';
      setAcctError(typeof msg === 'string' ? msg : 'Registration failed. Please try again.');
      toast.error(typeof msg === 'string' ? msg : 'Registration failed');
      setSubmitting(false);
      return false;
    }
    const d = { full_name: acct.name, country: acct.country };
    setData(prev => ({ ...d, ...prev }));
    await saveDraft(d, 1);
    setSubmitting(false);
    return true;
  };

  const goNext = async () => {
    if (step === 0) {
      if (!validateAccount()) return;
      if (await createAccount()) { setStep(1); window.scrollTo(0, 0); }
      return;
    }
    const cur = flowSteps[step];
    if (!validateOnboardingStep(cur, data)) return;
    if (step < flowSteps.length - 1) {
      saveDraft(data, step + 1);
      setStep(s => s + 1); window.scrollTo(0, 0); return;
    }
    await finish();
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...onboardingPayload(data),
        photos: { front: photos.front?.photo_url, side: photos.side?.photo_url, back: photos.back?.photo_url },
      };
      await api.post('/onboarding', { data: payload, plan, billing });
      const fresh = await refresh();
      if (!fresh?.onboarding_completed) throw new Error('not saved');
      const dest = postOnboardingPath(fresh);
      toast.success(dest.startsWith('/checkout') ? 'Profile saved — complete your payment to activate.' : 'Welcome to YourReGen!');
      nav(dest, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Your profile could not be saved. Please try again — your answers are kept.');
      setSubmitting(false);
    }
  };

  // Once the account exists, Back never returns to the account form.
  const back = () => {
    if (step > 1) { setStep(s => s - 1); window.scrollTo(0, 0); }
    else if (step === 0) { window.location.href = '/#plans'; }
  };

  const cur = flowSteps[step];
  const isAccount = step === 0;
  const isLast = step === flowSteps.length - 1;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-5 sm:px-8 md:px-12 pt-10 bg-background text-foreground" data-testid="onboard-flow" style={{ paddingBottom: 'calc(9rem + var(--cookie-bar, 0px))' }}>
      <div className="flex items-center justify-between">
        <RegenLogo size="sm" />
        <div className="label-mini text-muted-foreground" data-testid="flow-step-counter">Step {step + 1} of {flowSteps.length}</div>
      </div>

      {planLabel && (
        <div className="mt-4 flex items-center gap-2 text-xs rounded-xl border border-border bg-card px-3 py-2" data-testid="selected-plan-banner">
          <span className="label-mini text-[#00B4C7]">Selected</span>
          <span className="font-semibold">{planLabel}{billingLabel(plan, billing) ? ` · ${billingLabel(plan, billing)}` : ''}</span>
        </div>
      )}

      <div className="mt-4 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-[#00C8FF] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-8 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-[#00C8FF]/12 text-[#00B4C7]">
          {isAccount ? <UserPlus className="w-5 h-5" /> : <cur.icon className="w-5 h-5" style={{ color: cur.color }} />}
        </div>
        <div>
          <div className="label-mini text-[#00B4C7]">{isAccount ? 'Create your account' : 'Onboarding'}</div>
          <h1 className="font-display font-black text-3xl tracking-tight" data-testid="flow-step-title">{isAccount ? 'Your details' : cur.title}</h1>
        </div>
      </div>

      <div className="mt-7 space-y-6" data-testid={`flow-step-${step}`}>
        {isAccount ? (
          <AccountStep acct={acct} setAcct={setAcct} confirm={confirm} setConfirm={setConfirm}
            consent={consent} setConsent={setConsent} pwValid={pwValid} pwMatch={pwMatch}
            showCode={showCode} setShowCode={setShowCode} code={code} setCode={setCode} error={acctError} />
        ) : cur.photos ? (
          <PhotoStep photos={photos} setPhotos={setPhotos} />
        ) : (
          <StepFields cur={cur} data={data} set={set} />
        )}
      </div>

      <div className="fixed left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-8 pb-6 px-5" style={{ bottom: 'var(--cookie-bar, 0px)' }}>
        <div className="max-w-5xl mx-auto flex gap-3 sm:px-3 md:px-7">
          {step !== 1 && (
            <Button onClick={back} variant="outline" data-testid="flow-back-btn" className="h-13 px-5 border-border rounded-2xl">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={goNext} disabled={submitting} data-testid="flow-next-btn"
            className="flex-1 h-13 bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-2xl">
            {isLast ? (submitting ? 'Saving…' : 'Submit & Finish') : (submitting ? 'Please wait…' : 'Continue')}
            {!submitting && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AccountStep({ acct, setAcct, confirm, setConfirm, consent, setConsent, pwValid, pwMatch, showCode, setShowCode, code, setCode, error }) {
  return (
    <>
      {error && (
        <div className="rounded-xl border border-[#FF5C5C]/40 bg-[#FF5C5C]/10 px-4 py-3 text-sm" data-testid="acct-error">
          {error}{/already registered/i.test(error) && <> — <a href="/login" className="text-[#00B4C7] font-semibold underline" data-testid="acct-error-login-link">Log in instead</a></>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-5">
      <div>
        <Label htmlFor="name">Full Name</Label>
        <Input id="name" data-testid="acct-name" value={acct.name} onChange={e => setAcct({ ...acct, name: e.target.value })} className="mt-1.5 bg-card border-border h-12" />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" data-testid="acct-email" value={acct.email} onChange={e => setAcct({ ...acct, email: e.target.value })} className="mt-1.5 bg-card border-border h-12" />
      </div>
      <div>
        <Label htmlFor="phone">Phone</Label>
        <Input id="phone" data-testid="acct-phone" value={acct.phone} onChange={e => setAcct({ ...acct, phone: e.target.value })} className="mt-1.5 bg-card border-border h-12" />
      </div>
      <div>
        <Label htmlFor="country">Country</Label>
        <Input id="country" list="of-country-list" data-testid="acct-country" value={acct.country} onChange={e => setAcct({ ...acct, country: e.target.value })} placeholder="Start typing your country…" className="mt-1.5 bg-card border-border h-12" />
        <datalist id="of-country-list">{COUNTRIES.map(c => <option key={c} value={c} />)}</datalist>
      </div>
      <div>
        <Label htmlFor="password">Password</Label>
        <Input id="password" type="password" data-testid="acct-password" value={acct.password} onChange={e => setAcct({ ...acct, password: e.target.value })} minLength={8} className="mt-1.5 bg-card border-border h-12" />
        <p className={`text-xs mt-1.5 ${acct.password && !pwValid ? 'text-[#FFB800]' : 'text-muted-foreground'}`}>Minimum 8 characters, including one uppercase letter and one number.</p>
      </div>
      <div>
        <Label htmlFor="confirm">Confirm Password</Label>
        <Input id="confirm" type="password" data-testid="acct-confirm" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1.5 bg-card border-border h-12" />
        {confirm.length > 0 && (
          <div className={`text-[11px] mt-1.5 flex items-center gap-1.5 ${pwMatch ? 'text-[#2f9e44]' : 'text-[#FF5C5C]'}`}>
            {pwMatch ? <><Check className="w-3.5 h-3.5" /> Passwords match</> : <><X className="w-3.5 h-3.5" /> Passwords do not match</>}
          </div>
        )}
      </div>
      </div>

      <div className="space-y-3 rounded-2xl bg-card border border-border p-4">
        <div className="label-mini text-[#00B4C7]">Required</div>
        <ConsentCheck testid="acct-consent-legal" checked={consent.legal} onChange={v => setConsent({ ...consent, legal: v })}>
          I have read and agree to the <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-[#00B4C7] underline">Terms of Service</a> and <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#00B4C7] underline">Privacy Policy</a>.
        </ConsentCheck>
        <ConsentCheck testid="acct-consent-health" checked={consent.health} onChange={v => setConsent({ ...consent, health: v })}>
          I explicitly consent to YourReGen processing my health and fitness data to provide my personalized experience.
        </ConsentCheck>
        <div className="label-mini text-muted-foreground pt-1">Optional</div>
        <ConsentCheck testid="acct-consent-marketing" checked={consent.marketing} onChange={v => setConsent({ ...consent, marketing: v })}>
          Send me coaching tips, product updates and offers by email.
        </ConsentCheck>
      </div>

      {!showCode ? (
        <button type="button" onClick={() => setShowCode(true)} data-testid="acct-have-code-btn" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#00B4C7]">
          <Ticket className="w-3.5 h-3.5" /> Have an access code?
        </button>
      ) : (
        <div data-testid="acct-code-section">
          <Label htmlFor="acct-code">Access Code</Label>
          <Input id="acct-code" data-testid="acct-code-input" value={code} onChange={e => setCode(e.target.value)} placeholder="ENTER CODE" className="mt-1.5 bg-card border-border h-12 uppercase font-mono" />
        </div>
      )}
    </>
  );
}

function ConsentCheck({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer text-sm leading-snug">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testid}
        className="mt-0.5 w-5 h-5 flex-shrink-0 rounded border-border accent-[#00C8FF] cursor-pointer" />
      <span className="text-foreground/85">{children}</span>
    </label>
  );
}
